import { componentService } from 'js/services/component.service';
import React, { memo } from 'react';
import { useFormatCurrency } from './hooks';
import * as dateUtils from 'js/utils/date.util';
import { compose, omit, prop } from 'ramda';
import { useJackpotsSelector } from 'js/store/hooks';
import { branch, defaultProps, lifecycle, mapProps, renderNothing, withHandlers, withProps } from 'recompose';

export const withJackpot = compose(
    mapProps(({ args, code, ...rest }) => {
        const jackpots = useJackpotsSelector(prop('jackpots'));

        return {
            jackpot: jackpots[args || code],
            code,
            ...rest
        };
    }),
    branch(
        props => !props.jackpot || !props.jackpot.amount,
        renderNothing
    )
);

export const withCasinoJackpot = compose(
    mapProps(({ args, code, ...rest }) => {
        const casino = useJackpotsSelector(prop('casino'));

        return {
            jackpot: casino,
            code,
            ...rest
        };
    }),
    branch(
        props => !props.jackpot || !props.jackpot.amount,
        renderNothing
    )
);

export const withJackpotRef = compose(
    defaultProps({
        className: '',
        interval: 100,
        pause: false,
        enableHitTime: true
    }),
    withProps(() => ({
        jackpotStartTime: useJackpotsSelector(prop('jackpotStartTime')),
        formatCurrency: useFormatCurrency()
    })),
    withHandlers(({ interval, enableHitTime, jackpot, formatCurrency }) => {
        let isReachLimit = false;
        let isHitTimeEnded = !jackpot.hitTime;
        let data = {};
        let jackpotRef;
        let frame;

        const updateAmount = (now) => {
            const { step, amount, originAmount, amountStartTime } = data;
            const timeDiff = now - amountStartTime;
            const newAmount = amount + (timeDiff / interval * step || 0);

            // if jackpot reach limit
            if (originAmount <= newAmount) {
                isReachLimit = true;

                return {
                    amount: originAmount
                };
            }

            if (timeDiff >= interval && amount !== newAmount) {
                return {
                    amount: newAmount,
                    amountStartTime: now
                };
            }
        };

        const updateCountdownTimer = (now) => {
            const { showHitTime, hitTimeStartTime, changeTypeStartTime } = data;
            let { tempHitTime } = data;

            // if hitTime not provided
            if (!tempHitTime) {
                return {};
            }

            // if hitTime reach limit
            if (tempHitTime < 1000) {
                isHitTimeEnded = true;
                isReachLimit = true;

                return {
                    showHitTime: false
                };
            }

            // Change hitTime every sec if timer is visible
            if (showHitTime && now - hitTimeStartTime >= 1000) {
                tempHitTime -= now - hitTimeStartTime;

                return {
                    tempHitTime,
                    hitTime: tempHitTime,
                    hitTimeStartTime: now
                };
            }

            // Change visibility of timer every 5 sec
            if (now - changeTypeStartTime >= 5000) {
                return {
                    changeTypeStartTime: now,
                    showHitTime: !showHitTime
                };
            }
        };

        const tick = () => {
            const now = Date.now();
            const newState = {
                ...updateAmount(now),
                ...updateCountdownTimer(now)
            };

            if (jackpotRef && Object.keys(newState).length) {
                data = { ...data, ...newState };

                jackpotRef.textContent = enableHitTime && data.showHitTime
                    ? dateUtils.getTime(data.hitTime)
                    : formatCurrency(data);
            }
        };

        const updateAnimation = () => {
            cancelAnimationFrame(frame);

            tick();
            frame = (!isReachLimit || !isHitTimeEnded) && requestAnimationFrame(updateAnimation);
        };

        return {
            setJackpotRef: () => ref => jackpotRef = ref,
            startAnimation: ({ jackpotStartTime }) => () => {
                const startTime = jackpotStartTime || Date.now();
                const { hitTime } = jackpot;
                const tempHitTime = data.tempHitTime
                    ? data.tempHitTime
                    : hitTime
                        ? new Date(hitTime * 1000).getTime() - startTime
                        : null;

                data = {
                    ...jackpot,
                    amountStartTime: startTime,

                    showHitTime: false,
                    hitTime: null,
                    tempHitTime,
                    hitTimeStartTime: startTime,
                    changeTypeStartTime: startTime
                };

                updateAnimation();
            },
            cancelAnimation: () => () => cancelAnimationFrame(frame)
        };
    }),
    lifecycle({
        componentDidMount() {
            !this.props.pause && this.props.startAnimation();
        },
        componentDidUpdate(prevProps) {
            const { pause, startAnimation, cancelAnimation } = this.props;

            if (pause !== prevProps.pause) {
                pause
                    ? cancelAnimation()
                    : startAnimation();
            }
        },
        componentWillUnmount() {
            this.props.cancelAnimation();
        }
    }),
    mapProps(omit(['jackpot', 'startAnimation', 'cancelAnimation']))
);

export const JackpotBare = memo(({ className, code, setJackpotRef }) =>
    <span
        className={className}
        data-jackpot-code={code}
        ref={setJackpotRef}
    />
);

/**
 * This is a regular component that shows a jackpot per code.
 */
export const Jackpot = compose(
    withJackpot,
    withJackpotRef
)(JackpotBare);

/**
 * Historically this is a component that shows Total Casino Jackpot.
 */
export const Jackpots = compose(
    withCasinoJackpot,
    withJackpotRef
)(JackpotBare);

componentService.registerWebContentComponents({ Jackpot, Jackpots });
